import {Badge} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Card} from 'src/design/atoms/Card';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React from 'react';
import type {
  JobHazardAnalysisStepResponse,
  RiskAssessmentCodeResponse,
} from 'src/api/ProactApiClient';
import {DeleteActionIcon} from 'src/design/molecules/DeleteActionIcon';
import {EditActionIcon} from 'src/design/molecules/EditActionIcon';
import {
  getRiskColor,
  getRiskLabel,
  safetyOptions,
} from './jobHazardAnalysisHelpers';

export interface JobHazardAnalysisStepListProps {
  steps: JobHazardAnalysisStepResponse[];
  mode: 'edit' | 'create' | 'view';
  onStepEdit: (offlineId?: string) => void;
  onStepDelete: (offlineId?: string) => void;
  riskMatrix?: RiskAssessmentCodeResponse;
}

export const JobHazardAnalysisStepList: React.FC<
  JobHazardAnalysisStepListProps
> = ({steps, mode, onStepEdit, onStepDelete, riskMatrix}) => {
  if (steps.length === 0) {
    return (
      <Card withBorder p="xl" style={{borderStyle: 'dashed'}}>
        <Text ta="center" c="dimmed" size="sm">
          {mode === 'view'
            ? 'No job steps have been added.'
            : 'No job steps added yet. Use the button above to add a step.'}
        </Text>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      {steps.map((step, index) => {
        const beforeColor = getRiskColor(riskMatrix, step.racBeforeControls);
        const beforeLabel = getRiskLabel(riskMatrix, step.racBeforeControls);
        const afterColor = getRiskColor(riskMatrix, step.racAfterControls);
        const afterLabel = getRiskLabel(riskMatrix, step.racAfterControls);
        const hasBeforeRisk = beforeLabel !== '—';
        const hasAfterRisk = afterLabel !== '—';

        return (
          <Card withBorder shadow="xs" key={step.offlineId} padding={0}>
            <Group
              justify="space-between"
              p="md"
              style={{borderBottom: '1px solid var(--mantine-color-gray-3)'}}>
              <Group gap="sm">
                <Badge variant="filled" color="gray" size="sm" circle>
                  {index + 1}
                </Badge>
                <Text fw={600} size="sm">
                  {step.name}
                </Text>
              </Group>
              {mode === 'edit' && (
                <Group gap="xs">
                  <EditActionIcon
                    onClick={() => onStepEdit(step.offlineId)}
                    tooltipText="Edit step"
                  />
                  <DeleteActionIcon
                    onClick={() => onStepDelete(step.offlineId)}
                    tooltipText="Delete step"
                  />
                </Group>
              )}
            </Group>

            <Stack p="md" gap="md">
              {step.potentialHazard && (
                <Box>
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                    Potential Hazards
                  </Text>
                  <Text size="sm">{step.potentialHazard}</Text>
                </Box>
              )}

              <SimpleGrid cols={2}>
                <Box
                  p="sm"
                  style={{
                    borderRadius: 'var(--mantine-radius-md)',
                    backgroundColor: hasBeforeRisk
                      ? `var(--mantine-color-${beforeColor}-1)`
                      : 'var(--mantine-color-gray-1)',
                    border: `1px solid var(--mantine-color-${
                      hasBeforeRisk ? beforeColor : 'gray'
                    }-3)`,
                    textAlign: 'center',
                  }}>
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                    RAC Before Controls
                  </Text>
                  {hasBeforeRisk ? (
                    <Badge color={beforeColor} variant="filled" size="lg">
                      {beforeLabel}
                    </Badge>
                  ) : (
                    <Text c="dimmed" size="sm">
                      —
                    </Text>
                  )}
                </Box>
                <Box
                  p="sm"
                  style={{
                    borderRadius: 'var(--mantine-radius-md)',
                    backgroundColor: hasAfterRisk
                      ? `var(--mantine-color-${afterColor}-1)`
                      : 'var(--mantine-color-gray-1)',
                    border: `1px solid var(--mantine-color-${
                      hasAfterRisk ? afterColor : 'gray'
                    }-3)`,
                    textAlign: 'center',
                  }}>
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                    RAC After Controls
                  </Text>
                  {hasAfterRisk ? (
                    <Badge color={afterColor} variant="filled" size="lg">
                      {afterLabel}
                    </Badge>
                  ) : (
                    <Text c="dimmed" size="sm">
                      —
                    </Text>
                  )}
                </Box>
              </SimpleGrid>

              {step.safetyControls?.some(
                sc => sc.safetyControlsTitle !== ''
              ) && (
                <Box>
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                    Safety Controls
                  </Text>
                  <Stack gap="xs">
                    {step.safetyControls
                      .filter(sc => sc.safetyControlsTitle !== '')
                      .map(sc => (
                        <Group key={sc.id} gap="sm" wrap="nowrap">
                          <Badge
                            variant="outline"
                            size="sm"
                            style={{flexShrink: 0}}>
                            {safetyOptions[Number(sc.safetyControlsTitle) - 1]}
                          </Badge>
                          {sc.safetyNotes && (
                            <Text size="sm" c="dimmed" lineClamp={1}>
                              {sc.safetyNotes}
                            </Text>
                          )}
                        </Group>
                      ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
};
